/**
 * FILE: apps/hub/src/components/enroll/InstallmentPlanFields.tsx
 * PURPOSE: Optional installment plan editor used during enrollment.
 */

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import type { InstallmentFormEntry } from '@/pages/enroll/enrollConstants'
import { X } from 'lucide-react'

interface InstallmentPlanFieldsProps {
  values: InstallmentFormEntry[]
  onChange: (values: InstallmentFormEntry[]) => void
  disabled?: boolean
}

export function InstallmentPlanFields({ values, onChange, disabled }: InstallmentPlanFieldsProps) {
  function addInstallment() {
    onChange([...values, { amount: undefined as unknown as number, dueDate: '', notes: '' }])
  }

  function updateInstallment(index: number, patch: Partial<InstallmentFormEntry>) {
    const next = values.map((entry, i) => (i === index ? { ...entry, ...patch } : entry))
    onChange(next)
  }

  function removeInstallment(index: number) {
    onChange(values.filter((_, i) => i !== index))
  }

  return (
    <div className="space-y-3 rounded-lg border border-border bg-card p-4">
      <div className="flex items-center justify-between">
        <h3 className="font-medium">Installment plan</h3>
        <Button type="button" size="sm" variant="outline" onClick={addInstallment} disabled={disabled}>
          Add installment
        </Button>
      </div>

      {values.length === 0 ? (
        <p className="text-sm text-muted-foreground">No installments. Click “Add installment” if this enrollment has a payment plan.</p>
      ) : (
        <div className="space-y-3">
          {values.map((entry, index) => (
            <div key={index} className="grid gap-3 rounded-md border border-border bg-background p-3">
              <div className="flex items-start justify-between gap-2">
                <p className="text-sm font-medium">Installment {index + 1}</p>
                <Button
                  type="button"
                  size="icon"
                  variant="ghost"
                  className="h-6 w-6"
                  onClick={() => removeInstallment(index)}
                  disabled={disabled}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
              <div className="grid gap-3 md:grid-cols-2">
                <div>
                  <Label>Amount</Label>
                  <Input
                    type="number"
                    inputMode="numeric"
                    min={1}
                    disabled={disabled}
                    value={entry.amount ?? ''}
                    onChange={(event) => updateInstallment(index, { amount: Number(event.target.value) })}
                  />
                </div>
                <div>
                  <Label>Due Date</Label>
                  <Input
                    type="date"
                    disabled={disabled}
                    value={entry.dueDate}
                    onChange={(event) => updateInstallment(index, { dueDate: event.target.value })}
                  />
                </div>
              </div>
              <div>
                <Label>Notes</Label>
                <Input
                  disabled={disabled}
                  value={entry.notes ?? ''}
                  onChange={(event) => updateInstallment(index, { notes: event.target.value })}
                  placeholder="Optional notes"
                />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
